// Floating bottom-right account pill for signed-in visitors on the
// marketing surfaces (Product Hunt landing + Pricing page).
//
// Purpose: give a signed-in user context about their plan without
// touching the existing page UI. The pill is purely additive — it
// floats above the page, dismissable, and only renders when there's
// a user. Signed-out visitors see nothing.
//
// The pill collapses to a small chip by default; clicking expands it
// into a panel with plan details + quick actions (Go to Dashboard,
// Manage billing, Continue checkout, etc.) depending on state.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ArrowRight, LayoutDashboard, Sparkles, Clock,
  CreditCard, Crown, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useUserPlanState } from '@/hooks/useUserPlanState';

interface FloatingAccountPillProps {
  // Pages this is mounted on (producthunt / pricing) — used to label the
  // "Continue checkout" CTA when the user has a pending PH offer.
  context?: 'producthunt' | 'pricing';
}

export function FloatingAccountPill({ context }: FloatingAccountPillProps) {
  const planState = useUserPlanState();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('fly_account_pill_dismissed') === '1';
  });

  // Render nothing if we shouldn't show:
  //   - signed-out (no account context to surface)
  //   - still loading auth (avoid layout flicker on cold load)
  //   - signed-in but never started a trial/plan ('no_agency') — these
  //     users are effectively first-time visitors who happened to OAuth
  //     for an unrelated reason; the page UI should still look like the
  //     marketing pitch they came for, not a logged-in dashboard hint
  //   - user explicitly dismissed the pill this session
  if (
    planState.state === 'loading' ||
    planState.state === 'unauthenticated' ||
    planState.state === 'no_agency' ||
    dismissed
  ) {
    return null;
  }

  // Resolve plan summary chips per state — small reusable strings shown
  // in both the collapsed chip and the expanded panel header.
  const stateConfig = resolveStateConfig(planState, context);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('fly_account_pill_dismissed', '1');
    } catch {
      /* storage denied — fine, just for this page load */
    }
  };

  const user = 'user' in planState ? planState.user : null;
  const avatarUrl = user?.avatarUrl;
  const displayName = user?.displayName ?? 'You';
  const initials = (displayName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase() || 'U';

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 max-w-[calc(100vw-2rem)]">
      {expanded ? (
        // ─── Expanded panel ──────────────────────────────────────────
        <div className="w-[340px] max-w-full rounded-2xl border border-white/10 bg-[#13131c] shadow-2xl shadow-black/40 overflow-hidden">
          {/* Header with avatar + dismiss */}
          <div className="flex items-center justify-between p-4 border-b border-white/8">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar className="h-9 w-9 shrink-0">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
                <AvatarFallback className="text-xs bg-violet-100 text-violet-900">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{displayName}</div>
                <div className="text-[11px] text-white/50 truncate">{user?.email ?? ''}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Collapse"
              className="text-white/40 hover:text-white/80 p-1 -m-1 transition"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          {/* Plan summary */}
          <div className="p-4 space-y-3">
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 ${stateConfig.bg}`}>
              <stateConfig.icon className={`h-4 w-4 shrink-0 ${stateConfig.iconColor}`} />
              <div className="text-xs leading-snug text-white/90 flex-1 min-w-0">
                <div className="font-semibold">{stateConfig.headline}</div>
                {stateConfig.sub ? <div className="text-white/55 mt-0.5">{stateConfig.sub}</div> : null}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              {stateConfig.primary ? (
                <Button
                  asChild
                  size="sm"
                  className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white shadow-lg shadow-violet-500/30"
                >
                  <Link to={stateConfig.primary.href}>
                    {stateConfig.primary.label}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
              ) : null}
              <Button
                asChild
                size="sm"
                variant="outline"
                className="w-full border-white/15 bg-white/5 text-white hover:bg-white/10 hover:border-white/30"
              >
                <Link to="/fly-app/lead-gen-tools">
                  <LayoutDashboard className="mr-1.5 h-4 w-4" />
                  Go to Dashboard
                </Link>
              </Button>
              {stateConfig.secondary ? (
                <Link
                  to={stateConfig.secondary.href}
                  className="text-center text-xs text-white/55 hover:text-white/80 transition py-1"
                >
                  {stateConfig.secondary.label}
                </Link>
              ) : null}
            </div>

            {/* Dismiss option (subtle) */}
            <button
              type="button"
              onClick={handleDismiss}
              className="w-full text-[10px] text-white/35 hover:text-white/60 transition pt-1"
            >
              Hide for this session
            </button>
          </div>
        </div>
      ) : (
        // ─── Collapsed pill ──────────────────────────────────────────
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={`Account: ${stateConfig.headline}`}
          className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-[#13131c]/95 backdrop-blur-md px-3 py-2 shadow-xl shadow-black/40 hover:bg-[#1a1a26] transition"
        >
          <Avatar className="h-6 w-6 shrink-0">
            {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
            <AvatarFallback className="text-[10px] bg-violet-100 text-violet-900">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-1.5 text-xs text-white/90 font-medium pr-1">
            <stateConfig.icon className={`h-3.5 w-3.5 ${stateConfig.iconColor}`} />
            <span className="whitespace-nowrap">{stateConfig.shortLabel}</span>
          </div>
          <ChevronUp className="h-3.5 w-3.5 text-white/40 group-hover:text-white/70 transition" />
        </button>
      )}
    </div>
  );
}

// ─── Per-state copy & visuals ────────────────────────────────────────

interface StateConfig {
  shortLabel: string;
  headline: string;
  sub: string | null;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  bg: string;
  primary: { label: string; href: string } | null;
  secondary: { label: string; href: string } | null;
}

function resolveStateConfig(
  planState: ReturnType<typeof useUserPlanState>,
  context: 'producthunt' | 'pricing' | undefined,
): StateConfig {
  // On the Product Hunt funnel page we keep CTAs inside the PH offer
  // flow — "See plans / Reactivate" routes back to /producthunt#pricing
  // instead of /pricing, because the user explicitly entered via that
  // funnel and we want them to stay there (the PH coupon + pricing math
  // is applied automatically on that page). The #pricing anchor scrolls
  // straight to the plan cards instead of landing them at the top of
  // the page — matches the post-OAuth `/producthunt#pricing` target the
  // funnel bootstrap uses for the same "pick a plan" intent.
  const pricingHref = context === 'producthunt' ? '/producthunt#pricing' : '/pricing';

  switch (planState.state) {
    // 'no_agency' intentionally not handled — the pill hides for that
    // state entirely (first-time signed-in users see the normal marketing
    // page exactly as a logged-out visitor would).
    case 'trial_active': {
      const { daysLeft, creditsLeft, creditsTotal, sub } = planState;
      return {
        shortLabel: `${sub.plan_slug === 'free' ? 'Trial' : sub.plan_slug} · ${daysLeft}d left`,
        headline: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your trial`,
        sub: `${creditsLeft.toLocaleString()} of ${creditsTotal.toLocaleString()} credits left.`,
        icon: Clock,
        iconColor: 'text-amber-300',
        bg: 'bg-amber-500/10 border border-amber-400/20',
        primary: { label: 'Upgrade to keep going', href: '/fly-app/lead-gen-subscription' },
        secondary: { label: 'Continue trial', href: '/fly-app/lead-gen-tools' },
      };
    }
    case 'ph_pending':
      return {
        shortLabel: 'Complete PH checkout',
        headline: 'Your Product Hunt offer is locked in',
        sub: 'Complete checkout to activate Growth — first month free.',
        icon: Sparkles,
        iconColor: 'text-fuchsia-300',
        bg: 'bg-fuchsia-500/10 border border-fuchsia-400/20',
        primary: {
          label: 'Complete checkout',
          href: '/fly-app/agency-checkout?plan=growth&cycle=monthly&coupon=PRODUCTHUNT',
        },
        secondary: null,
      };
    case 'trial_expired':
      return {
        shortLabel: 'Trial ended',
        headline: 'Your 7-day trial has ended',
        sub: 'Pick a plan to keep using Fly Social.',
        icon: AlertCircle,
        iconColor: 'text-red-300',
        bg: 'bg-red-500/10 border border-red-400/20',
        primary: { label: 'Pick a plan', href: pricingHref },
        secondary: { label: 'Open dashboard (read-only)', href: '/fly-app/lead-gen-tools' },
      };
    case 'active': {
      const { sub, creditsLeft, creditsTotal, renewsAt } = planState;
      const renewLabel = renewsAt
        ? new Date(renewsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
        : null;
      return {
        shortLabel: `On ${cap(sub.plan_slug)}`,
        headline: `You're on the ${cap(sub.plan_slug)} plan`,
        sub: `${creditsLeft.toLocaleString()} / ${creditsTotal.toLocaleString()} credits${renewLabel ? ` · renews ${renewLabel}` : ''}`,
        icon: CreditCard,
        iconColor: 'text-emerald-300',
        bg: 'bg-emerald-500/10 border border-emerald-400/20',
        primary: null,
        secondary: { label: 'Manage billing', href: '/fly-app/billing' },
      };
    }
    case 'lifetime': {
      const { creditsLeft, creditsTotal } = planState;
      return {
        shortLabel: 'Lifetime',
        headline: 'Welcome back, Lifetime member',
        sub: `${creditsLeft.toLocaleString()} / ${creditsTotal.toLocaleString()} lifetime credits left.`,
        icon: Crown,
        iconColor: 'text-amber-300',
        bg: 'bg-amber-500/10 border border-amber-400/20',
        primary: null,
        secondary: null,
      };
    }
    case 'cancelled':
      return {
        shortLabel: 'Reactivate',
        headline: 'Your subscription ended',
        sub: 'Welcome back — pick a plan to continue.',
        icon: AlertCircle,
        iconColor: 'text-red-300',
        bg: 'bg-red-500/10 border border-red-400/20',
        primary: { label: 'Reactivate', href: pricingHref },
        secondary: null,
      };
    default:
      return {
        shortLabel: 'Account',
        headline: 'Welcome back',
        sub: null,
        icon: Sparkles,
        iconColor: 'text-violet-300',
        bg: 'bg-violet-500/10 border border-violet-400/20',
        primary: null,
        secondary: null,
      };
  }
}

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
